import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderSlot as _renderSlot, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 2,
  class: "flex w-64 flex-col fixed h-full"
}
const _hoisted_2 = { class: "md:pl-64 flex flex-col flex-1" }
const _hoisted_3 = {
  key: 1,
  class: "flex gap-x-4 w-full pl-2 pr-6"
}
const _hoisted_4 = { class: "flex-1" }
const _hoisted_5 = { class: "overflow-auto" }

import { ref, computed, onMounted, watch } from "vue"
import GgmsNavigation from "@/components/GgmsNavigation.vue"
import GgmsTaskSessionBar from "@/components/GgmsTaskSessionBar.vue"
import AddLeadModal from "@/components/AddLeadModal.vue"
import EmailVerificationModal from "@/components/email-verification/EmailVerificationModal.vue"
import PhoneCallSidebar from "@/components/phone-sidebar/PhoneCallSidebar.vue"
import PhoneOnCallBar from "@/components/phone-sidebar/PhoneOnCallBar.vue"
import NotificationSidebar from "@/components/notifications/NotificationsSidebar.vue"
import FilterSidebar from "@/components/filters/FilterSidebar.vue"
import MobileSidebarAndHeader from "@/components/MobileSidebarAndheader.vue"
import MenuActionItems from "@/components/MenuActionItems.vue"
import GgmsInput from "@/components/GgmsInput.vue"

import {
    SearchIcon,
    HomeIcon,
    UsersIcon,
    InboxInIcon,
    TagIcon,
    ClipboardCheckIcon,
    ChartBarIcon,
    CogIcon,
    DocumentTextIcon,
    PresentationChartLineIcon,
    ClipboardListIcon,
    RefreshIcon,
    FireIcon,
    OfficeBuildingIcon,
} from "@heroicons/vue/outline"
import CodeSquareIcon from "@/components/icons/CodeSquareIcon.vue"

import { getUser, deleteCookie, getCookieValue, hasUserPermission } from "@/shared/utils/helpers"
import { useTaskStore } from "@/stores/task"
import { useTagStore } from "@/stores/tag"
import { useAuthStore } from "@/stores/auth"
import { useAgencyStore } from "@/stores/agency"
import { usePhoneCallStore } from "@/stores/phone-call"
import { useConfigStore } from "@/stores/config"
import { useAgentsStore } from "@/stores/agents"
import { useSocketStore } from "@/stores/socket"
import { useLeadsStore } from "@/stores/leads"
import { useOriginStore } from "@/stores/origin"
import { PhoneCall } from "@/shared/models/phone-call"
import { ResourceTypeName, Role } from "@/shared/models/role"


export default /*#__PURE__*/_defineComponent({
  __name: 'GgmsSidebarAndHeader',
  setup(__props) {

const taskStore = useTaskStore()
const tagStore = useTagStore()
const authStore = useAuthStore()
const agencyStore = useAgencyStore()
const phoneCallStore = usePhoneCallStore()
const configStore = useConfigStore()
const agentsStore = useAgentsStore()
const socketStore = useSocketStore()
const leadStore = useLeadsStore()
const originStore = useOriginStore()

let navigation = [
    { name: "Dashboard", icon: HomeIcon, path: "/dashboard" },
    { name: "People", icon: UsersIcon, path: "/people" },
    { name: "Listings", icon: OfficeBuildingIcon, path: "/listings" },
    { name: "Inbox", icon: InboxInIcon, path: "/inbox" },
    { name: "Deals", icon: TagIcon, path: "/deals" },
    { name: "Tasks", icon: ClipboardCheckIcon, path: "/tasks" },
    { name: "Reports", icon: ChartBarIcon, path: "/reports" },
    {
        name: "Templates",
        icon: DocumentTextIcon,
        path: "/templates",
    },
    {
        name: "Workflows",
        icon: FireIcon,
        path: "/workflows",
    },
    { name: "Forms", icon: ClipboardListIcon, path: "/forms" },
    {
        name: "Developers",
        icon: CodeSquareIcon,
        children: [
            {
                name: "API Keys",
                path: "/developers",
            },
        ],
    },
    {
        name: "Settings",
        icon: CogIcon,
        children: [
            {
                name: "My Settings",
                path: "/my-settings",
            },
            {
                name: "Agencies",
                path: "/agencies",
            },
            {
                name: "MLS Feeds",
                path: "/mls-feeds",
            },
            {
                name: "Domains",
                path: "/domains",
            },
            {
                name: "Properties",
                path: "/properties",
            },
            {
                name: "Phone Numbers",
                path: "/phone-numbers",
            },
            {
                name: "Tags",
                path: "/tags",
            },
            {
                name: "Origins",
                path: "/origins",
            },
            {
                name: "Lists",
                path: "/lists",
            },
            {
                name: "Themes",
                path: "/themes",
            },
            {
                name: "Integrations",
                path: "/integrations",
            },
        ],
    },
    { name: "Team", icon: UsersIcon, path: "/team" },
    { name: "Audit", icon: PresentationChartLineIcon, path: "/audit" },
    { name: "Event Logs", icon: PresentationChartLineIcon, path: "/logs" },
    { name: "Jobs", icon: RefreshIcon, path: "/jobs" },
]

const isDataFetched = ref(false)
const isPhoneSidebarOpen = ref(false)
const isNotificationSidebarOpen = ref(false)
const isAgencyNewlyRegistered = ref(false)
const searchTerm = ref("")

const isPhoneCallInProgress = computed(() => ["calling", "ongoing"].includes(phoneCallStore.phoneCallState))
const isPhoneTopBarVisible = computed(() => isPhoneCallInProgress.value && !isPhoneSidebarOpen.value)
const mainTopBarTopClass = computed(() => (isPhoneTopBarVisible.value ? "top-[66px]" : "top-0"))
const isAddLeadModalOpen = computed(() => leadStore.isAddLeadModalOpen)
const phoneCallLead = computed(() => phoneCallStore.currentPhoneCall?.leads?.[0])
const currentUser = computed(() => authStore.currentUser)
const isTaskSessionStarted = computed(() => taskStore.isActiveTaskSession)

const isMobileScreen = computed(() => window.screen.width <= 768)

async function openAddLeadModal() {
    tagStore.tableState.typeName = "lead"
    await originStore.getAllOrigins()
    leadStore.isAddLeadModalOpen = true
}

function closeAddLeadModal() {
    leadStore.isAddLeadModalOpen = false
    phoneCallStore.currentPhoneCall = {} as Partial<PhoneCall>
}

function openPhoneSidebar() {
    if (phoneCallStore.phoneCallState === "idle") {
        phoneCallStore.getCalls()
        tagStore.getPhoneCallTags()
    }
    isPhoneSidebarOpen.value = true
}

function closeCall() {
    phoneCallStore.phoneCallState = "finished"
    phoneCallStore.disconnectTwilio()
}

function openNotificationSidebar() {
    isNotificationSidebarOpen.value = true
}

function closeEmailVerificationModal() {
    isAgencyNewlyRegistered.value = false
    deleteCookie("isAgencyNewlyRegistered")
}

function checkIfAgencyIsNewlyRegistered() {
    const isAgencyNewlyRegisteredCookie = getCookieValue("isAgencyNewlyRegistered")

    // if the email is confirmed, emailConfirmation will be undefined and we explicitly check the value for false
    isAgencyNewlyRegistered.value =
        currentUser.value?.emailConfirmation?.confirmed === false && isAgencyNewlyRegisteredCookie === "true"
}

onMounted(async () => {
    await Promise.all([
        agencyStore.loadAgency(),
        agencyStore.getAvailableAgencies(),
        taskStore.getActiveTaskSession(),
        configStore.getConfig(),
        agentsStore.getMe(),
        tagStore.getCachedTags(),
        originStore.getCachedOrigins(),
        socketStore.connectSocket(),
        socketStore.connectJobs(),
        agentsStore.loadAgents(),
    ])
    const user = getUser()
    authStore.currentUser = user
    isDataFetched.value = true
    // filter out those navigation items that doesn't have permission
    navigation = navigation.filter((item) => hasUserPermission(user?.role as Role, item.name as ResourceTypeName))
    checkIfAgencyIsNewlyRegistered()
})

watch(
    () => leadStore.isAddLeadModalOpen,
    (value) => {
        if (!value) {
            return
        }
        isPhoneSidebarOpen.value = false
        phoneCallStore.phoneCallState = "idle"
        isNotificationSidebarOpen.value = false
    }
)

return (_ctx: any,_cache: any) => {
  return (isDataFetched.value)
    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
        (isPhoneTopBarVisible.value)
          ? (_openBlock(), _createBlock(PhoneOnCallBar, {
              key: 0,
              onOnCloseCall: closeCall,
              onOnOpenSidebar: openPhoneSidebar
            }))
          : _createCommentVNode("", true),
        (isTaskSessionStarted.value)
          ? (_openBlock(), _createBlock(GgmsTaskSessionBar, { key: 1 }))
          : _createCommentVNode("", true),
        (!isMobileScreen.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createVNode(GgmsNavigation, { items: _unref(navigation) }, null, 8, ["items"])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", {
            class: _normalizeClass(["sticky z-40 flex-shrink-0 flex h-fit bg-white shadow", mainTopBarTopClass.value])
          }, [
            (isMobileScreen.value)
              ? (_openBlock(), _createBlock(MobileSidebarAndHeader, {
                  key: 0,
                  navigation: _unref(navigation),
                  onOpenPhoneSidebar: openPhoneSidebar,
                  onOpenAddLeadModal: openAddLeadModal,
                  onOpenNotificationSidebar: openNotificationSidebar
                }, null, 8, ["navigation"]))
              : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createVNode(GgmsInput, {
                    modelValue: searchTerm.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
                    placeholder: "Search",
                    iconStart: _unref(SearchIcon),
                    inputClass: "border-0 shadow-transparent h-16 focus:ring-0 focus:border-0",
                    class: "w-full"
                  }, null, 8, ["modelValue", "iconStart"]),
                  _createVNode(MenuActionItems, {
                    onOpenPhoneSidebar: openPhoneSidebar,
                    onOpenAddLeadModal: openAddLeadModal,
                    onOpenNotificationSidebar: openNotificationSidebar
                  })
                ]))
          ], 2),
          _createElementVNode("main", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _renderSlot(_ctx.$slots, "default")
            ])
          ])
        ]),
        _createVNode(AddLeadModal, {
          open: isAddLeadModalOpen.value,
          lead: phoneCallLead.value,
          onCloseModal: _cache[1] || (_cache[1] = ($event: any) => (closeAddLeadModal()))
        }, null, 8, ["open", "lead"]),
        _createVNode(PhoneCallSidebar, {
          modelValue: isPhoneSidebarOpen.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isPhoneSidebarOpen).value = $event))
        }, null, 8, ["modelValue"]),
        _createVNode(NotificationSidebar, {
          modelValue: isNotificationSidebarOpen.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((isNotificationSidebarOpen).value = $event))
        }, null, 8, ["modelValue"]),
        _createVNode(FilterSidebar),
        _createVNode(EmailVerificationModal, {
          open: isAgencyNewlyRegistered.value,
          onCloseModal: closeEmailVerificationModal
        }, null, 8, ["open"])
      ], 64))
    : _createCommentVNode("", true)
}
}

})